import React, { useEffect, useState } from "react";
import { isValidInventoryQuantity, normalizeInventoryQuantity } from "./inventory-quantity";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { inventoryCorrectionsApi } from "../../../api/inventory_corrections";
import { inventoryStoragesApi } from "../../../api/inventory_storages";
import {
  ClipboardDocumentListIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  ArrowLeftIcon,
  PlusIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Button, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { axiosWithToast } from "../../../components/Utils";
import { CorrectionAddProductDialog } from "../../../components/dashboard/inventory/CorrectionAddProductDialog";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { TextField } from "@mui/material";
import toast from "react-hot-toast";

export const InventoryCorrection = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [correction, setCorrection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [storages, setStorages] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [editingDate, setEditingDate] = useState(false);

  useEffect(() => {
    fetchCorrection();
    fetchStorages();
  }, [id]);

  const fetchStorages = async () => {
    try {
      const response = await inventoryStoragesApi.getInventoryStorages({ active: true });
      if (response?.success && response?.data) {
        setStorages(response.data);
      }
    } catch (err) {
      console.error("Error fetching storages:", err);
    }
  };

  const fetchCorrection = async () => {
    try {
      setLoading(true);
      const response = await inventoryCorrectionsApi.getInventoryCorrection(id);
      if (response?.data?.success) {
        setCorrection(response.data.data);
      } else {
        setError("Kunne ikke hente lagerkorreksjon");
      }
    } catch (err) {
      console.error("Error fetching correction:", err);
      setError("Kunne ikke hente lagerkorreksjon");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    await axiosWithToast(
      inventoryCorrectionsApi.approveInventoryCorrection(id, ""),
      () => fetchCorrection(),
      () => {},
      () => {},
      "Godkjenner lagerkorreksjon..."
    );
  };

  const handleReject = async () => {
    const comment = prompt("Skriv inn avslagsgrunn (valgfritt):");
    if (comment === null) return;

    await axiosWithToast(
      inventoryCorrectionsApi.rejectInventoryCorrection(id, comment || ""),
      () => fetchCorrection(),
      () => {},
      () => {},
      "Avslår lagerkorreksjon..."
    );
  };

  const handleDelete = async () => {
    if (!confirm("Er du sikker på at du vil slette denne lagerkorreksjonen?")) return;

    await axiosWithToast(
      inventoryCorrectionsApi.deleteInventoryCorrection(id),
      () => {
        navigate("/dashboard/inventory/corrections");
      },
      () => {},
      () => {},
      "Sletter lagerkorreksjon..."
    );
  };

  const handleSubmitForApproval = async () => {
    await axiosWithToast(
      inventoryCorrectionsApi.submitInventoryCorrectionForApproval(id),
      () => fetchCorrection(),
      () => {},
      () => {},
      "Sender lagerkorreksjon til godkjenning..."
    );
  };

  const handleAddItem = async (product, formData) => {
    if (!product) {
      toast.error("Velg et produkt");
      return;
    }

    // Default values - will be edited later in the list view
    const defaultQuantity = 1;
    const defaultFromStorageId = storages.length > 0 ? storages[0].id : null;

    if (!defaultFromStorageId) {
      toast.error("Ingen lager tilgjengelig");
      return;
    }

    try {
      const response = await inventoryCorrectionsApi.addItemToCorrection(id, {
        product_id: product.id,
        quantity: defaultQuantity,
        from_storage_id: defaultFromStorageId,
        to_storage_id: null,
        note: formData.note || "",
      });

      if (response?.data?.success) {
        // Update only the items array instead of reloading the whole page
        const newItem = response.data.data;
        setCorrection({
          ...correction,
          items: [...(correction.items || []), newItem],
          total_items: (correction.total_items || 0) + 1,
          total_quantity: (correction.total_quantity || 0) + (newItem.quantity || 0),
        });
        // Automatically set the new item in edit mode so user can set quantity and storage
        setEditingItem(newItem.id);
        toast.success("Produkt lagt til - sett antall og lager");
      } else {
        const errorMessage = response?.data?.errors?.join(", ") || "Kunne ikke legge til produkt";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error adding item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || error.response?.data?.error || "Kunne ikke legge til produkt";
      toast.error(errorMessage);
      throw error; // Re-throw so dialog doesn't close on error
    }
  };


  const handleUpdateItem = async (item) => {
    if (!item.from_storage_id) {
      toast.error("Velg fra-lager");
      return;
    }
    // to_storage_id is optional
    if (item.to_storage_id && item.from_storage_id === item.to_storage_id) {
      toast.error("Fra-lager og til-lager må være forskjellige");
      return;
    }
    if (!isValidInventoryQuantity(item.quantity)) {
      toast.error("Antall må være større enn 0");
      return;
    }

    setUpdatingItem(item.id);
    try {
      const response = await inventoryCorrectionsApi.updateItemInCorrection(id, item.id, {
        quantity: normalizeInventoryQuantity(item.quantity),
        from_storage_id: item.from_storage_id,
        to_storage_id: item.to_storage_id || null,
        note: item.note || "",
      });

      if (response?.data?.success) {
        // Update only the specific item instead of reloading the whole page
        const updatedItem = response.data.data;
        const updatedItems = correction.items.map((i) =>
          i.id === item.id ? updatedItem : i
        );
        
        // Recalculate totals
        const totalQuantity = updatedItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
        
        setCorrection({
          ...correction,
          items: updatedItems,
          total_items: updatedItems.length,
          total_quantity: totalQuantity,
        });
        
        setEditingItem(null);
        setUpdatingItem(null);
        toast.success("Linje oppdatert");
      } else {
        const errorMessage = response?.data?.errors?.join(", ") || "Kunne ikke oppdatere linje";
        toast.error(errorMessage);
        setUpdatingItem(null);
      }
    } catch (error) {
      console.error("Error updating item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || error.response?.data?.error || "Kunne ikke oppdatere linje";
      toast.error(errorMessage);
      setUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!confirm("Er du sikker på at du vil fjerne denne linjen?")) return;

    try {
      const response = await inventoryCorrectionsApi.removeItemFromCorrection(id, itemId);
      if (response?.data?.success) {
        // Update only the items array instead of reloading the whole page
        const removedItem = correction.items.find((i) => i.id === itemId);
        const updatedItems = correction.items.filter((i) => i.id !== itemId);
        setCorrection({
          ...correction,
          items: updatedItems,
          total_items: updatedItems.length,
          total_quantity: Math.max(0, (correction.total_quantity || 0) - (removedItem?.quantity || 0)),
        });
        toast.success("Linje fjernet");
      } else {
        const errorMessage = response?.data?.errors?.join(", ") || "Kunne ikke fjerne linje";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error removing item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || error.response?.data?.error || "Kunne ikke fjerne linje";
      toast.error(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "draft":
        return <PencilIcon className="h-4 w-4 text-gray-400" />;
      case "pending_approval":
        return <DocumentCheckIcon className="h-4 w-4 text-yellow-500" />;
      case "approved":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <TrashIcon className="h-4 w-4 text-red-500" />;
      case "completed":
        return <CheckCircleIcon className="h-4 w-4 text-blue-500" />;
      default:
        return <ClipboardDocumentListIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return "Utkast";
      case "pending_approval":
        return "Venter på godkjenning";
      case "approved":
        return "Godkjent";
      case "rejected":
        return "Avslått";
      case "completed":
        return "Fullført";
      default:
        return status;
    }
  };

  const headerActions = [
    {
      component: null,
      label: "Tilbake",
      description: "",
      path: null,
      action: () => {
        navigate("/dashboard/inventory/corrections");
      },
      icon: ArrowLeftIcon,
      textColor: "text-black",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    ...(correction?.can_edit && correction?.status === "draft"
      ? [
          {
            component: null,
            label: "Send til godkjenning",
            description: "",
            path: null,
            action: handleSubmitForApproval,
            icon: DocumentCheckIcon,
            textColor: "text-blue-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : []),
    ...(correction?.can_approve && correction?.status === "pending_approval"
      ? [
          {
            component: null,
            label: "Godkjenn",
            description: "",
            path: null,
            action: handleApprove,
            icon: CheckCircleIcon,
            textColor: "text-green-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
          {
            component: null,
            label: "Avslå",
            description: "",
            path: null,
            action: handleReject,
            icon: TrashIcon,
            textColor: "text-red-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : []),
    ...(correction?.can_delete
      ? [
          {
            component: null,
            label: "Slett",
            description: "",
            path: null,
            action: handleDelete,
            icon: TrashIcon,
            textColor: "text-red-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <>
        <DashboardHeader title="Lagerkorreksjon" subtitle="Laster..." />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error || !correction) {
    return (
      <>
        <DashboardHeader title="Lagerkorreksjon" subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:8">
          <ResourceError errors={[error || "Kunne ikke hente lagerkorreksjon"]} />
        </div>
      </>
    );
  }

  return (
    <>
      <DashboardHeader
        title={correction.title || "Lagerkorreksjon"}
        subtitle={`Status: ${getStatusLabel(correction.status)}`}
        actions={headerActions}
        meta={[
          {
            component: (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                  correction.status
                )}`}
              >
                {getStatusIcon(correction.status)}
                <span className="ml-1">{getStatusLabel(correction.status)}</span>
              </span>
            ),
            label: "Status",
            description: "",
            path: null,
            action: null,
            icon: null,
            hidden: false,
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          {/* Correction Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Korreksjonsnummer</dt>
                <dd className="mt-1 text-sm text-gray-900">{correction.correction_number || "-"}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Dato</dt>
                {editingDate && correction?.can_edit && correction?.status === "draft" ? (
                  <div className="mt-1 flex items-center gap-2">
                    <TextField
                      type="date"
                      size="small"
                      value={
                        correction.correction_date
                          ? new Date(correction.correction_date).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={(e) => {
                        setCorrection({
                          ...correction,
                          correction_date: e.target.value ? new Date(e.target.value).toISOString().split("T")[0] : correction.correction_date,
                        });
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 200 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={async () => {
                        try {
                          const response = await inventoryCorrectionsApi.updateInventoryCorrection(id, {
                            title: correction.title,
                            description: correction.description,
                            correction_date: correction.correction_date,
                          });
                          if (response?.data?.success) {
                            setEditingDate(false);
                            toast.success("Dato oppdatert");
                          } else {
                            toast.error("Kunne ikke oppdatere dato");
                          }
                        } catch (error) {
                          console.error("Error updating date:", error);
                          const errorMessage = error.response?.data?.errors?.join(", ") || "Kunne ikke oppdatere dato";
                          toast.error(errorMessage);
                        }
                      }}
                    >
                      Lagre
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setEditingDate(false);
                        fetchCorrection();
                      }}
                    >
                      Avbryt
                    </Button>
                  </div>
                ) : (
                  <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    {correction.correction_date
                      ? new Date(correction.correction_date).toLocaleDateString("nb-NO")
                      : "-"}
                    {correction?.can_edit && correction?.status === "draft" && (
                      <button
                        onClick={() => setEditingDate(true)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Rediger dato"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </dd>
                )}
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Antall linjer</dt>
                <dd className="mt-1 text-sm text-gray-900">{correction.total_items || 0}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Total mengde</dt>
                <dd className="mt-1 text-sm text-gray-900">{correction.total_quantity || 0}</dd>
              </div>
            </div>

            {correction.description && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Beskrivelse</dt>
                <dd className="mt-1 text-sm text-gray-900">{correction.description}</dd>
              </div>
            )}

            {correction.created_by && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Opprettet av</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {correction.created_by.name} - {new Date(correction.created_at).toLocaleDateString("nb-NO")}
                </dd>
              </div>
            )}

            {correction.approved_by && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">Godkjent av</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {correction.approved_by.name} -{" "}
                  {correction.approved_at
                    ? new Date(correction.approved_at).toLocaleDateString("nb-NO")
                    : ""}
                </dd>
              </div>
            )}

            {correction.approval_comment && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">Godkjenningskommentar</dt>
                <dd className="mt-1 text-sm text-gray-900">{correction.approval_comment}</dd>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Korreksjonslinjer</h3>
              {correction?.can_edit && correction?.status === "draft" && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlusIcon className="h-5 w-5" />}
                  onClick={() => setAddItemDialogOpen(true)}
                >
                  Legg til linje
                </Button>
              )}
            </div>
            {correction.items && correction.items.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                    <TableRow>
                      <TableCell>Produkt</TableCell>
                      <TableCell align="right">Antall</TableCell>
                      <TableCell>Fra lager</TableCell>
                      <TableCell>Til lager (valgfritt)</TableCell>
                      <TableCell>Notat</TableCell>
                      {correction?.can_edit && correction?.status === "draft" && (
                        <TableCell align="right">Handlinger</TableCell>
                      )}
                    </TableRow>
                </TableHead>
                <TableBody>
                  {correction.items.map((item) => (
                    <TableRow key={item.id} hover>
                      {editingItem === item.id ? (
                        <>
                          <TableCell>
                            {item.product?.displayTitle || item.product?.title || `Produkt ${item.product?.id}`}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity || ""}
                              onChange={(e) => {
                                const updatedItems = correction.items.map((i) =>
                                  i.id === item.id ? { ...i, quantity: e.target.value } : i
                                );
                                setCorrection({ ...correction, items: updatedItems });
                              }}
                              inputProps={{ min: 0.0001, step: 0.0001 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Fra lager</InputLabel>
                              <Select
                                value={item.from_storage_id || ""}
                                label="Fra lager"
                                onChange={(e) => {
                                  const updatedItems = correction.items.map((i) =>
                                    i.id === item.id ? { ...i, from_storage_id: e.target.value } : i
                                  );
                                  setCorrection({ ...correction, items: updatedItems });
                                }}
                              >
                                {storages.map((storage) => (
                                  <MenuItem key={storage.id} value={storage.id}>
                                    {storage.storage_number} - {storage.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Til lager</InputLabel>
                              <Select
                                value={item.to_storage_id || ""}
                                label="Til lager"
                                onChange={(e) => {
                                  const updatedItems = correction.items.map((i) =>
                                    i.id === item.id ? { ...i, to_storage_id: e.target.value || null } : i
                                  );
                                  setCorrection({ ...correction, items: updatedItems });
                                }}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>Ingen</em>
                                </MenuItem>
                                {storages.map((storage) => (
                                  <MenuItem key={storage.id} value={storage.id}>
                                    {storage.storage_number} - {storage.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.note || ""}
                              onChange={(e) => {
                                const updatedItems = correction.items.map((i) =>
                                  i.id === item.id ? { ...i, note: e.target.value } : i
                                );
                                setCorrection({ ...correction, items: updatedItems });
                              }}
                              placeholder="Notat"
                              sx={{ minWidth: 200 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleUpdateItem(item)}
                                disabled={updatingItem === item.id}
                              >
                                {updatingItem === item.id ? "Lagrer..." : "Lagre"}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setEditingItem(null);
                                  fetchCorrection();
                                }}
                                disabled={updatingItem === item.id}
                              >
                                Avbryt
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {item.product?.displayTitle || item.product?.title || `Produkt ${item.product?.id}`}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell>
                            {item.from_storage?.storage_number} - {item.from_storage?.title}
                          </TableCell>
                          <TableCell>
                            {item.to_storage
                              ? `${item.to_storage.storage_number} - ${item.to_storage.title}`
                              : "- (Kun korreksjon i fra-lager)"}
                          </TableCell>
                          <TableCell>{item.note || "-"}</TableCell>
                          {correction?.can_edit && correction?.status === "draft" && (
                            <TableCell align="right">
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={() => {
                                    // Ensure item has storage IDs for editing
                                    const itemToEdit = {
                                      ...item,
                                      from_storage_id: item.from_storage?.id || item.from_storage_id,
                                      to_storage_id: item.to_storage?.id || item.to_storage_id || null,
                                    };
                                    // Update the item in the items array with the storage IDs
                                    const updatedItems = correction.items.map((i) =>
                                      i.id === item.id ? itemToEdit : i
                                    );
                                    setCorrection({ ...correction, items: updatedItems });
                                    setEditingItem(item.id);
                                  }}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Rediger"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleRemoveItem(item.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Fjern"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen linjer</h3>
                <p className="text-gray-500">Legg til linjer for å starte korreksjonen</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Item Dialog */}
      <CorrectionAddProductDialog
        open={addItemDialogOpen}
        onClose={() => setAddItemDialogOpen(false)}
        onProductSelected={handleAddItem}
        storages={storages}
      />
    </>
  );
};

